import MainFrame from "../MainFrame";
import Container from "@mui/material/Container";
import Title from "../common/Title";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import {
  ActivityLog,
  ActivityLogType,
  PaymentType,
  Staff,
  StaffDailySummary,
  StoreLocation,
} from "../common/models";
import {
  addOrUpdateActivityLog,
  deleteActivityLog,
  getActivityLogsByStoreLocationID,
} from "../common/api";
import { getLocalDate, getLocalTime } from "../common/helper";
import { Text } from "@aws-amplify/ui-react";
import minMax from "dayjs/plugin/minMax";
import { IUserContext, UserContext } from "../common/context";
import { DARK_COLOR } from "../common/constants";
dayjs.extend(minMax);

interface Column {
  id: "name" | "sales" | "total";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
  borderRadiusTopLeft?: number;
  borderRadiusTopRight?: number;
}

const columns: Column[] = [
  { id: "name", label: "Name", minWidth: 150, borderRadiusTopLeft: 6 },
  { id: "sales", label: "Sales", minWidth: 400 },
  { id: "total", label: "Total", minWidth: 150, borderRadiusTopRight: 6 },
];

interface SalesByStaffName {
  [key: string]: ActivityLog[];
}

interface AddSaleModalProps {
  title: string;
  onSuccess: () => void;
  selectedLocation: StoreLocation;
  activityLog?: ActivityLog | null;
  staffIDToStaff: { [key: string]: Staff };
}

const AddSaleModal = (props: AddSaleModalProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [alertText, setAlertText] = useState<string | null>(null);
  const [saleAmount, setSaleAmount] = useState(
    props.activityLog
      ? props.activityLog.saleAmountCard + props.activityLog.saleAmountCash
      : NaN
  );
  const [giftCardAmount, setGiftCardAmount] = useState(
    props.activityLog ? props.activityLog.giftCardAmount : NaN
  );
  const [depositAmount, setDepositAmount] = useState(
    props.activityLog ? props.activityLog.depositAmount : NaN
  );
  const [totalSalesAmount, setTotalSalesAmount] = useState(
    saleAmount + (giftCardAmount || 0) + (depositAmount || 0)
  );
  const [commAmount, setCommAmount] = useState(
    props.activityLog
      ? props.activityLog.commissionAmountCard +
          props.activityLog.commissionAmountCash
      : NaN
  );

  const [commPercentage, setCommPercentage] = useState<number>(NaN);
  const [tipAmount, setTipAmount] = useState(
    props.activityLog ? props.activityLog.tipAmountCard : NaN
  );
  const [saleCashOrCard, setSaleCashOrCard] = useState(
    props.activityLog && props.activityLog.saleAmountCash
      ? PaymentType.CASH
      : PaymentType.CARD
  );
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(
    props.activityLog && props.activityLog.staffID
      ? props.staffIDToStaff[props.activityLog?.staffID]
      : null
  );
  const [selectedDateTime, setSelectedDateTime] = useState(
    dayjs(props.activityLog?.createdAt) || dayjs()
  );

  const handleSaleCashOrCardChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSaleCashOrCard(
      (event.target as HTMLInputElement).value as unknown as PaymentType
    );
  };

  useEffect(() => {
    const totalSales =
      (saleAmount || 0) + (depositAmount || 0) + (giftCardAmount || 0);
    setTotalSalesAmount(totalSales);
    setCommPercentage(Math.ceil(10000 * commAmount / totalSales) / 10000);
  }, [saleAmount, depositAmount, giftCardAmount]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    setSelectedDateTime(dayjs(props.activityLog?.createdAt) || dayjs());
  }, [modalOpen]);

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
  };

  const onClose = () => {
    setSaleAmount(
      props.activityLog
        ? props.activityLog.saleAmountCard + props.activityLog.saleAmountCash
        : NaN
    );
    setGiftCardAmount(props.activityLog?.giftCardAmount);
    setDepositAmount(props.activityLog?.depositAmount);
    setCommAmount(
      props.activityLog
        ? props.activityLog.commissionAmountCard +
            props.activityLog.commissionAmountCash
        : NaN
    );
    setTipAmount(
      props.activityLog
        ? props.activityLog.tipAmountCard + props.activityLog.tipAmountCash
        : NaN
    );
    setSaleCashOrCard(
      props.activityLog && props.activityLog.saleAmountCash
        ? PaymentType.CASH
        : PaymentType.CARD
    );
    setSelectedStaff(
      props.activityLog && props.activityLog.staffID
        ? props.staffIDToStaff[props.activityLog?.staffID]
        : null
    );
    setAlertText(null);
    setModalOpen(false);
  };
  const activityLog = props.activityLog;

  return (
    <div>
      {activityLog ? (
        <Chip
          // color="primary"
          // color={"#847057"}
          sx={{
            width: 150,
            height: 150,
            marginRight: 1,
            marginTop: 1,
            // backgroundColor: "#f2d7c5",
            backgroundColor: DARK_COLOR,
            // color: "#847057",
          }}
          onClick={(event) => {
            setModalOpen(true);
          }}
          label={
            <section>
              <Text color={"yellow"}>{`Time: ${getLocalTime(
                activityLog.createdAt
              )}`}</Text>
              <Text color={"white"}>{`Sales: \$${
                activityLog.saleAmountCard +
                activityLog.saleAmountCash +
                (activityLog.giftCardAmount || 0) +
                (activityLog.depositAmount || 0)
              }`}</Text>
              <Text color={"white"}>{`Comms: \$${
                activityLog.commissionAmountCard || 0
              }`}</Text>
              <Text color={"white"}>{`Tip: \$${
                activityLog.tipAmountCard || 0
              }`}</Text>
            </section>
          }
        ></Chip>
      ) : (
        <Button
          variant="outlined"
          style={{ color: DARK_COLOR, borderColor: DARK_COLOR }}
          onClick={() => setModalOpen(true)}
        >
          Add Sale
        </Button>
      )}
      <Modal
        key={props?.activityLog?.id || "new"}
        open={modalOpen}
        onClose={onClose}
      >
        <Box sx={style}>
          <Grid container style={{ flex: 1 }}>
            <Grid style={{ flexGrow: 1 }}>
              <Title>{props.title}</Title>
            </Grid>
            {props.activityLog ? (
              <Button
                variant="contained"
                color="error"
                style={{
                  marginRight: 5,
                  justifyContent: "flex-end",
                }}
                onClick={() => {
                  if (props.activityLog && props.activityLog.id) {
                    deleteActivityLog(props.activityLog.id).then(() => {
                      onClose();
                    });
                  }
                }}
              >
                Delete
              </Button>
            ) : (
              <></>
            )}
          </Grid>

          <Collapse in={alertText != null}>
            <Alert severity="error">{alertText}</Alert>
          </Collapse>
          <Grid>
            <Autocomplete
              options={Object.values(props.staffIDToStaff)}
              getOptionLabel={(staff: Staff) => staff.name}
              id="controlled-demo"
              value={selectedStaff}
              onChange={(event: any, newValue: Staff | null) => {
                setSelectedStaff(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={true}
                  label="staff"
                  variant="standard"
                />
              )}
            />
          </Grid>
          <Grid container style={{ marginTop: 10 }}>
            <DateTimePicker
              value={selectedDateTime}
              onChange={(val) => {
                setSelectedDateTime(val || dayjs());
              }}
            />
          </Grid>
          <Grid container style={{ marginTop: 10 }}>
            <TextField
              style={{ width: 200 }}
              required
              id="filled-required"
              label="Received Amount ($)"
              value={saleAmount}
              type="number"
              onChange={(event) => {
                var eventValue = event.target.value
                const amount = parseFloat(eventValue);
                console.log(amount);
                if (amount >= 0 || eventValue == "") {
                  setSaleAmount(amount);
                  console.log("Sale Amount" + saleAmount)
                }
              }}
            />
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              value={saleCashOrCard}
              style={{ marginLeft: 10 }}
              onChange={handleSaleCashOrCardChange}
            >
              <FormControlLabel
                value={PaymentType.CARD}
                control={<Radio />}
                label="Card"
              />
              <FormControlLabel
                value={PaymentType.CASH}
                control={<Radio />}
                label="Cash"
              />
            </RadioGroup>
            <Grid container style={{ marginTop: 10 }}>
              <TextField
                style={{ width: 200 }}
                required
                id="filled-required"
                label="Deposit Amount ($)"
                value={depositAmount}
                type="number"
                onChange={(event) => {
                  const amount = parseFloat(event.target.value);
                  setDepositAmount(amount);
                }}
              />
              <TextField
                style={{ width: 200, marginLeft: 10 }}
                required
                id="filled-required"
                label="Gift Card Amount ($)"
                value={giftCardAmount}
                type="number"
                onChange={(event) => {
                  const amount = parseFloat(event.target.value);
                  setGiftCardAmount(amount);
                }}
              />
            </Grid>
            <TextField
              disabled={true}
              style={{
                width: 410,
                marginTop: 10,
              }}
              required
              id="filled-required"
              label="Total Sales Amount ($)"
              value={totalSalesAmount}
              type="number"
            />
          </Grid>
          <Divider style={{ marginTop: 10 }}></Divider>
          <Grid container style={{ marginTop: 10 }}>
            <TextField
              required
              id="filled-required"
              label="Commission Amount ($)"
              value={commAmount}
              type="number"
              onChange={(event) => {
                const amount = parseFloat(event.target.value);
                setCommAmount(amount);
                setCommPercentage(
                  parseFloat(((100 * amount) / totalSalesAmount).toFixed(2)) /
                    100
                );
              }}
            />
            <TextField
              style={{ marginLeft: 10 }}
              required
              id="filled-required"
              label="Commission %"
              value={100 * commPercentage}
              type="number"
              onChange={(event) => {
                const percentage = Number(event.target.value);
                if (percentage >= 0 && percentage < 100) {
                  setCommPercentage(percentage / 100);
                  setCommAmount(
                    parseFloat(
                      ((percentage * totalSalesAmount) / 100).toFixed(2)
                    )
                  );
                }
              }}
            />
          </Grid>
          <Divider style={{ marginTop: 10 }}></Divider>
          <Grid container style={{ marginTop: 10 }}>
            <TextField
              required
              id="filled-required"
              label="Card Tip Amount ($)"
              value={tipAmount}
              type="number"
              onChange={(event) => {
                setTipAmount(parseFloat(event.target.value));
              }}
            />
          </Grid>
          <Grid
            style={{
              marginTop: 10,
              justifyContent: "flex-end",
            }}
            container
          >
            <Button variant="outlined" color="error" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginLeft: 10 }}
              onClick={() => {
                if (selectedStaff == null) {
                  setAlertText("You must specify staff");
                  return;
                }
                if (saleAmount <= 0 || Number.isNaN(saleAmount)) {
                  setAlertText("Sales cannot be less than or equal to $0");
                  return;
                }

                if (saleAmount > 1000) {
                  setAlertText("Sales cannot be greater than $1000");
                  return;
                }

                if (tipAmount > 1000) {
                  setAlertText("Tip cannot be greater than $1000");
                  return;
                }

                if (commAmount > 1000) {
                  setAlertText("Commission cannot be greater than $1000");
                  return;
                }

                addOrUpdateActivityLog({
                  id: props.activityLog?.id,
                  staffID: selectedStaff.id,
                  type: ActivityLogType.SALE,
                  saleAmountCard:
                    saleCashOrCard == PaymentType.CARD ? saleAmount : 0,
                  saleAmountCash:
                    saleCashOrCard == PaymentType.CASH ? saleAmount : 0,
                  commissionAmountCard: commAmount,
                  commissionAmountCash: 0,
                  tipAmountCard: tipAmount,
                  tipAmountCash: 0,
                  giftCardAmount: giftCardAmount,
                  depositAmount: depositAmount,
                  storeLocationID: props.selectedLocation.id,
                  createdAt: selectedDateTime.toISOString(),
                }).then(() => {
                  props.onSuccess();
                  onClose();
                });
              }}
            >
              Save
            </Button>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

export default function FrontDeskPage() {
  const { staffs, storeLocations } = useContext<IUserContext>(UserContext);
  const [activityLogUpdated, setActivityLogUpdated] = useState(0);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [selectedDate, setSelectedDate] = useState(getLocalDate(dayjs()));
  const handleChange = (e: any) => {
    setSelectedLocationIndex(e.target.value);
  };

  useEffect(() => {
    if (storeLocations.length == 0) {
      return;
    }
    getActivityLogsByStoreLocationID(
      storeLocations[selectedLocationIndex].id,
      selectedDate
    ).then((activityLogs: ActivityLog[]) => {
      setActivityLogs(activityLogs);
    });
  }, [selectedDate, selectedLocationIndex, activityLogUpdated, storeLocations]);

  const salesByStaffName: SalesByStaffName = activityLogs.reduce(
    (acc: SalesByStaffName, sale: ActivityLog) => {
      const staffId = sale.staffID;
      if (!acc[staffId]) {
        acc[staffId] = [];
      }
      acc[staffId].push(sale);
      return acc;
    },
    {}
  );

  const staffIDToStaff: { [key: string]: Staff } = staffs.reduce(
    (acc: { [key: string]: Staff }, staff: Staff) => {
      acc[staff.id] = staff;
      return acc;
    },
    {}
  );

  const totalActivityLog: StaffDailySummary = activityLogs.reduce(
    (
      acc: StaffDailySummary,
      sale: ActivityLog
    ): StaffDailySummary => {
      return {
        date: dayjs().toISOString(),
        checkedInAt:
          sale.type == ActivityLogType.CHECK_IN
            ? sale.createdAt
            : acc.checkedInAt,
        checkedOutAt:
          sale.type == ActivityLogType.CHECK_OUT
            ? sale.createdAt
            : acc.checkedOutAt,
        numberOfSales: acc.numberOfSales + 1,
        totalSalesCard:
          acc.totalSalesCard +
          sale.saleAmountCard +
          sale.saleAmountCash +
          (sale.giftCardAmount || 0) +
          (sale.depositAmount || 0),
        totalSalesCash:
          acc.totalSalesCash + sale.saleAmountCash,
        totalGiftCard: acc.totalGiftCard + (sale.giftCardAmount || 0),
        totalDeposit: acc.totalDeposit + (sale.depositAmount || 0),
        totalCommisionCard:
          acc.totalCommisionCard + sale.commissionAmountCard,
        totalCommisionCash:
          acc.totalCommisionCash + sale.commissionAmountCash,
        totalTipCard: acc.totalTipCard + sale.tipAmountCard,
        totalTipCash: acc.totalTipCash + sale.tipAmountCash,
      };
    },
    {
      date: dayjs().toISOString(),
      checkedInAt: null,
      checkedOutAt: null,
      numberOfSales: 0,
      totalSalesCard: 0,
      totalSalesCash: 0,
      totalCommisionCard: 0,
      totalCommisionCash: 0,
      totalGiftCard: 0,
      totalDeposit: 0,
      totalTipCard: 0,
      totalTipCash: 0,
    }
  );

  return (
    <MainFrame>
      <Title>Sales for the day</Title>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container maxWidth="lg">
          <Grid container>
            <FormControl>
              <InputLabel id="demo-simple-select-label">Location</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedLocationIndex}
                label="Location"
                onChange={handleChange}
              >
                {storeLocations.map((row, index) => {
                  return <MenuItem value={index}>{row.name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid container paddingTop={3}>
            <DatePicker
              value={selectedDate}
              onChange={(value) => {
                setSelectedDate(value || dayjs());
              }}
            ></DatePicker>
          </Grid>
          <Grid container paddingTop={3} paddingBottom={2}>
            <AddSaleModal
              title={"Add Sale"}
              onSuccess={() => setActivityLogUpdated(activityLogUpdated + 1)}
              selectedLocation={storeLocations[selectedLocationIndex]}
              staffIDToStaff={staffIDToStaff}
            ></AddSaleModal>
          </Grid>
          <Grid paddingTop={1}>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{
                          minWidth: column.minWidth,
                          backgroundColor: DARK_COLOR,
                          color: "white",
                          borderTopLeftRadius: column.borderRadiusTopLeft,
                          borderTopRightRadius: column.borderRadiusTopRight,
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(salesByStaffName).map((row) => {
                    const staffID = row[0];
                    const activityLogs = row[1].sort((a, b) =>
                      a.createdAt > b.createdAt ? 1 : -1
                    );
                    if (activityLogs.length === 0) {
                      return <></>;
                    }
                    const total: StaffDailySummary = activityLogs.reduce(
                      (
                        acc: StaffDailySummary,
                        sale: ActivityLog
                      ): StaffDailySummary => {
                        return {
                          date: dayjs().toISOString(),
                          checkedInAt:
                            sale.type == ActivityLogType.CHECK_IN
                              ? sale.createdAt
                              : acc.checkedInAt,
                          checkedOutAt:
                            sale.type == ActivityLogType.CHECK_OUT
                              ? sale.createdAt
                              : acc.checkedOutAt,
                          numberOfSales: acc.numberOfSales + 1,
                          totalSalesCard:
                            acc.totalSalesCard +
                            sale.saleAmountCard +
                            sale.saleAmountCash +
                            (sale.giftCardAmount || 0) +
                            (sale.depositAmount || 0),
                          totalSalesCash:
                            acc.totalSalesCash + sale.saleAmountCash,
                          totalGiftCard: acc.totalGiftCard + (sale.giftCardAmount || 0),
                          totalDeposit: acc.totalDeposit + (sale.depositAmount || 0),
                          totalCommisionCard:
                            acc.totalCommisionCard + sale.commissionAmountCard,
                          totalCommisionCash:
                            acc.totalCommisionCash + sale.commissionAmountCash,
                          totalTipCard: acc.totalTipCard + sale.tipAmountCard,
                          totalTipCash: acc.totalTipCash + sale.tipAmountCash,
                        };
                      },
                      {
                        date: dayjs().toISOString(),
                        checkedInAt: null,
                        checkedOutAt: null,
                        numberOfSales: 0,
                        totalSalesCard: 0,
                        totalSalesCash: 0,
                        totalGiftCard: 0,
                        totalDeposit: 0,
                        totalCommisionCard: 0,
                        totalCommisionCash: 0,
                        totalTipCard: 0,
                        totalTipCash: 0,
                      }
                    );
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={staffID}
                      >
                        <TableCell
                          key={"name"}
                          color={DARK_COLOR}
                          style={{ borderColor: DARK_COLOR }}
                        >
                          {staffIDToStaff[staffID]?.name}
                        </TableCell>
                        <TableCell
                          style={{ borderColor: DARK_COLOR }}
                          key={staffID}
                        >
                          <Grid container>
                            {activityLogs
                              .filter(
                                (activityLog) =>
                                  activityLog.type === ActivityLogType.SALE
                              )
                              .map((activityLog, index) => {
                                return (
                                  <AddSaleModal
                                    key={activityLog.id}
                                    title={"Update Sale"}
                                    onSuccess={() =>
                                      setActivityLogUpdated(
                                        activityLogUpdated + 1
                                      )
                                    }
                                    selectedLocation={
                                      storeLocations[selectedLocationIndex]
                                    }
                                    activityLog={activityLogs[index]}
                                    staffIDToStaff={staffIDToStaff}
                                  ></AddSaleModal>
                                );
                              })}
                          </Grid>
                        </TableCell>
                        <TableCell style={{ borderColor: DARK_COLOR }}>
                          <Chip
                            color="default"
                            sx={{
                              width: 170,
                              height: 200,
                              marginRight: 1,
                              "& .MuiChip-label": {
                                display: "block",
                                whiteSpace: "normal",
                              },
                            }}
                            label={
                              <section>
                                <Text
                                  color={total.checkedInAt ? "blue" : "red"}
                                >{`Checked In: ${
                                  total.checkedInAt
                                    ? getLocalTime(total.checkedInAt)
                                    : "--"
                                }`}</Text>
                                <Text
                                  color={total.checkedOutAt ? "blue" : "red"}
                                >{`Checked Out: ${
                                  total.checkedOutAt
                                    ? getLocalTime(total.checkedOutAt)
                                    : "--"
                                }`}</Text>
                                <div>{`Total Sales: \$${total.totalSalesCard}`}</div>
                                <div>{`Comms: \$${total.totalCommisionCard}`}</div>
                                <div>{`Tip: \$${total.totalTipCard}`}</div>
                              </section>
                            }
                          ></Chip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={'grand-total'}
                      >
                        <TableCell
                          key={"name"}
                          color={DARK_COLOR}
                          style={{ borderColor: DARK_COLOR, fontStyle: 'bold', fontSize: 25 }}
                        >
                          Grand Total
                        </TableCell>
                        <TableCell
                          style={{ borderColor: DARK_COLOR }}
                          key={'grand-total'}
                        >
                        </TableCell>
                        <TableCell style={{ borderColor: DARK_COLOR }}>
                          <Chip
                            color="default"
                            sx={{
                              width: 170,
                              height: 200,
                              marginRight: 1,
                              "& .MuiChip-label": {
                                display: "block",
                                whiteSpace: "normal",
                              },
                            }}
                            label={
                              <section>
                                <div>{`Total Sales: \$${totalActivityLog.totalSalesCash + totalActivityLog.totalSalesCard}`}</div>
                                <div>{`- Sales (Cash): \$${totalActivityLog.totalSalesCash}`}</div>
                                <div>{`- Sales (Card): \$${totalActivityLog.totalSalesCard - totalActivityLog.totalGiftCard - totalActivityLog.totalDeposit}`}</div>
                                <div>{`- Gift Card: \$${totalActivityLog.totalGiftCard}`}</div>
                                <div>{`- Deposit: \$${totalActivityLog.totalDeposit}`}</div>
                                <div>{`Comms (Card): \$${totalActivityLog.totalCommisionCard}`}</div>
                                <div>{`Tips (Card): \$${totalActivityLog.totalTipCard}`}</div>
                              </section>
                            }
                          ></Chip>
                        </TableCell>
                      </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Container>
        <Snackbar
          anchorOrigin={{
            horizontal: "center",
            vertical: "top",
          }}
          open={activityLogUpdated > 0}
          onClose={() => setActivityLogUpdated(0)}
          autoHideDuration={3000}
        >
          <Alert
            onClose={() => setActivityLogUpdated(0)}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Activity Log Successfully Updated
          </Alert>
        </Snackbar>
      </LocalizationProvider>
    </MainFrame>
  );
}
